/**
 * 认证相关 API 服务
 * B端员工微信扫码登录
 */
import { request } from '@umijs/max';

/** 微信授权类型 */
export enum WxAuthType {
  /** 静默授权，只获取 openid */
  SNSAPI_BASE = 1,
  /** 获取用户信息 */
  SNSAPI_USERINFO = 2,
  /** 网页登录 */
  SNSAPI_LOGIN = 3,
  /** 二维码登录 */
  QRCODE_LOGIN = 4,
}

/** 获取授权 URL 请求参数 */
export interface AuthUrlParams {
  /** 微信 AppId */
  appId: string;
  /** 回调 URL */
  url?: string;
  /** 授权类型 */
  type: WxAuthType;
}

/** 获取授权 URL 响应 */
export interface AuthUrlResult {
  /** 授权 URL */
  url: string;
  /** 授权码（用于轮询） */
  code?: string;
}

/** 微信登录请求参数 */
export interface WxLoginParams {
  /** 微信 AppId */
  appId: string;
  /** 授权码 */
  code: string;
  /** 状态参数 */
  state?: string;
  /** 授权类型 */
  type: WxAuthType;
}

/** 微信登录响应 */
export interface WxLoginResult {
  /** 令牌名称 */
  tokenName: string;
  /** 令牌值 */
  tokenValue: string;
  /** 令牌超时时间（秒） */
  tokenTimeout: number;
  /** 微信 OpenId */
  openId: string;
}

/** 用户账号信息 */
export interface UserAccount {
  /** 子用户ID */
  subUserId: number;
  /** 账号ID */
  accountId: number;
  /** 账号名称 */
  accountName: string;
  /** 是否选中 */
  isSelect?: boolean;
}

/** 用户账号列表响应 */
export interface UserAccountListResult {
  /** 用户ID */
  loginId: number;
  /** 用户名称 */
  name?: string;
  /** 用户头像 */
  avatar?: string;
  /** 账号列表 */
  accounts: UserAccount[];
}

/** 切换账号请求参数 */
export interface SwitchAccountParams {
  /** 账号ID */
  accountId: number;
}

/**
 * Token 管理器
 */
export const TokenManager = {
  // 员工端 token
  getBizToken(): string | null {
    return localStorage.getItem('b_token');
  },
  setBizToken(token: string): void {
    localStorage.setItem('b_token', token);
  },
  removeBizToken(): void {
    localStorage.removeItem('b_token');
  },

  // 员工端 OpenId
  getBizOpenId(): string | null {
    return localStorage.getItem('b_openId');
  },
  setBizOpenId(openId: string): void {
    localStorage.setItem('b_openId', openId);
  },
  removeBizOpenId(): void {
    localStorage.removeItem('b_openId');
  },

  // 当前账号 ID
  getAccountId(): number | null {
    const id = localStorage.getItem('b_accountId');
    return id ? parseInt(id, 10) : null;
  },
  setAccountId(accountId: number): void {
    localStorage.setItem('b_accountId', String(accountId));
  },
  removeAccountId(): void {
    localStorage.removeItem('b_accountId');
  },

  // 清除所有认证信息
  clearAll(): void {
    this.removeBizToken();
    this.removeBizOpenId();
    this.removeAccountId();
  },

  // 检查是否已登录
  isLoggedIn(): boolean {
    return !!this.getBizToken();
  },
};

/**
 * 获取微信授权 URL
 * @param params 请求参数
 */
export async function getWxAuthUrl(params: AuthUrlParams): Promise<AuthUrlResult> {
  return request<AuthUrlResult>('/b/user/authUrlByWxMp', {
    method: 'POST',
    data: params,
  });
}

/**
 * 微信扫码登录
 * @param params 请求参数
 */
export async function wxLogin(params: WxLoginParams): Promise<WxLoginResult> {
  const result = await request<WxLoginResult>('/b/user/loginByWxMp', {
    method: 'POST',
    data: params,
  });

  // 登录成功后保存 token 和 openId
  if (result.tokenValue) {
    TokenManager.setBizToken(result.tokenValue);
  }
  if (result.openId) {
    TokenManager.setBizOpenId(result.openId);
  }

  return result;
}

/**
 * 获取用户账号列表
 */
export async function getUserAccountList(): Promise<UserAccountListResult> {
  return request<UserAccountListResult>('/b/user/accountList', {
    method: 'POST',
    data: {},
  });
}

/**
 * 切换账号
 * @param params 请求参数
 */
export async function switchAccount(params: SwitchAccountParams): Promise<void> {
  await request('/b/user/switchAccount', {
    method: 'POST',
    data: params,
  });

  // 切换成功后更新本地存储的 accountId
  TokenManager.setAccountId(params.accountId);
}

/**
 * 注销 Token
 */
export async function revokeToken(): Promise<void> {
  await request('/b/user/revokeToken', {
    method: 'POST',
    data: {},
  });
  TokenManager.clearAll();
}

/**
 * 退出登录
 */
export function logout(): void {
  TokenManager.clearAll();
  window.location.href = '/user/login';
}
